import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('atlas_token'));
  const [loading, setLoading] = useState(true);

  // Only fetch user on initial load, not on every token change
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('atlas_token');
      if (!storedToken) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          withCredentials: true
        });
        setUser(res.data);
        setToken(storedToken);
      } catch {
        localStorage.removeItem('atlas_token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []); // Only run once on mount

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('atlas_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, name, password) => {
    const res = await axios.post(`${API}/auth/register`, { email, name, password });
    localStorage.setItem('atlas_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  /**
   * Login with Google OAuth - exchanges session_id for token
   * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
   */
  const loginWithGoogle = async (sessionId) => {
    const res = await axios.post(`${API}/auth/google/callback`, 
      { session_id: sessionId },
      { withCredentials: true }
    );
    localStorage.setItem('atlas_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  /**
   * Redirect to Google OAuth directly (skips Emergent landing page)
   * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
   */
  const redirectToGoogleAuth = () => {
    // Redirect to /login after OAuth - the hash with session_id will be detected by AppRouter
    // Using /login instead of /dashboard to avoid ProtectedRoute interference
    const redirectUrl = window.location.origin + '/login';
    // Use provider=google to go directly to Google account selection
    window.location.href = `https://auth.emergentagent.com/?provider=google&redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
    } catch (err) {
      // Ignore logout errors
    }
    localStorage.removeItem('atlas_token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(res.data);
    } catch { /* ignore */ }
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, loginWithGoogle, redirectToGoogleAuth, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
